/*
 *   Copyright (c) 2026, CYBRA Corp. All rights reserved. 
 */

import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UserValidationDialogComponent } from "app/components/login-validation-dialog/user-validation-dialog.component";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { SimpleSuccessErrorDialogComponent, ERROR_DIALOG_TITLE } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { InstanceData } from "app/models/instance-data";
import { ReaderInstance } from "app/models/reader-instance";
import { ServiceInstance } from "app/models/service-instance";
import { AblenetExpReturnDevicesValidationService, AbleNetReaderStateEnum, ValidationInfoDto } from "app/services/ablenet.expreturndevices.service";
import { ReaderService } from "app/services/reader.service";


/** The delay between the API calls to refresh the information in the UI */
const RefreshDelayInMs = 1000;

/** Property Key on the AbleNet Exp Return Validation Service's "Reader" Property which is used to specify the Reader that should be controlled. */
const ServicePropertyKey_Reader = "associated_reader";

/** Property Key on Reader Instance's which is used for the Reader's Status. */
const ReaderPropertyKey_ReaderStatus = "reader_status";

/** Property Key on the AbleNet Exp Return Validation Service's "Use Handheld" Property which is used to specify if a handheld reader should be used. */
const ServicePropertyKey_UseHandheldReader = "use_handheld";


@Component({
  templateUrl: './ablenet.exp-return-devices.dashboard.html',
  styleUrls: ['./ablenet.exp-return-devices.dashboard.css']
})
export class AbleNetExpReturnDevicesDashboardComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) 
  indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog')
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

    /** Display the User Validation Dialog for Override option */
  @ViewChild('userValidationDialog')
  userValidationDialogComponent: UserValidationDialogComponent;

  /** Portal Instance which is performing the Shipment Validation and whose information is being shown */
  portal: ServiceInstance;

  /** Current State being reflected in the UI */
  state: ValidationInfoDto;

  /** Flag used to indicate if the component has been destroyed or not.  This is used by the background "Refresh Data" task to indicate when it should stop. */
  isDestroyed = false;

  /** Reader that is being controlled by the Portal */
  reader: ReaderInstance = null;

  /** Holds the current Reader State if it is known */
  readerState: AbleNetReaderStateEnum = null;

  /** Flag used to indicate if the Reader State has been retrieved or not. Used to disable some UI controls while the state is being retrieved.*/
  isReaderStateRetrieved: boolean = false;

  /** Flag used to indicate if any Reader or Order Actions are being performed.  This includes: [Start Reader, Stop Reader, Clear Order, Process Order] */
  isActionBeingPerformed: boolean = false;

  /** In case AbleNet asks to allow them select particular Assets only for processing */
  assets : AssetUI[] = [];

  stopProcessingClicked: boolean = false;

  /** Check if a handheld reader is being used & based on that make the required UI changes */
  isHandheldReaderUsed : boolean = false;

  /** Constructor */
  constructor(
    private ablenetExpReturnDevicesService: AblenetExpReturnDevicesValidationService,
    private readerService: ReaderService,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  /**
     * Initializes the component.  
     * Called automatically by the Angular Component Lifecycle.
     */
  ngOnInit(): void {
    console.log(`ngOnInit()`);
    /* 
     * Get the data that was resolved in the background and kickstart the 
     * "Refresh" task that keeps the UI up to date
     */
    this.route.data.subscribe((
      data: {
        portal: ServiceInstance,
        state: ValidationInfoDto,
      }
    ) => {
      // Update the portal / state values that were retrieved in the background
      this.portal = data?.portal;
      this.state = data?.state;

      // Get whether a Handheld Reader should be used instead, from the Portal's own "Use Handheld" Property
      let handheldDatum: InstanceData = this?.portal?.data?.find(d => d?.property?.property == ServicePropertyKey_UseHandheldReader);
      this.isHandheldReaderUsed = handheldDatum != null ? handheldDatum.value == 'true' : false;

       if(this.isHandheldReaderUsed) {
        console.log("Handheld Reader is being used for this Portal. Start the Validation Attempt");
        this.startReader();
      } else {
        // Get the Reader that is configured to be controlled by the Portal
        let datum: InstanceData = this?.portal?.data?.find(datum => datum?.property?.property == ServicePropertyKey_Reader);
        this.reader = datum != null ? datum.object_value.reader_reference : null;
        if (this.reader == null || this.reader == undefined) {
          this.handleError(`The Portal is not configured with a Reader to control.  Please configure the Portal with a Reader and try again.`);
        }
        console.log("Antenna/Reader isbeing used for this Portal. Start the Reader");
        this.refreshReaderState();
      }

      // Start the background task to refresh the data being displayed
      setTimeout(this.refreshData.bind(this), RefreshDelayInMs);
    });
  }

  /**
 * Destroys the component.  
 * Called automatically by the Angular Component Lifecycle.
 */
  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  /**
 * Refreshes the UI Display.  Constantly called by background tasks every so often
 * to keep the UI up to date with the latest changes / reads. 
 */
  private async refreshData(): Promise<void> {
    // If the Component has been destroyed, then just return as there's nothing to do
    if (this.isDestroyed) {
      return;
    }

    // Call the API to get the current state of the Portal...
    let state: ValidationInfoDto = null;
    try {
      state = await this.ablenetExpReturnDevicesService.getCurrentState(this.portal.id);
    } catch (error) {
      console.error("Problem obtaining current state of Portal.", error);
    }
    console.log(JSON.stringify(state));
    // Update the current state if needed and tell angular that it needs to update the UI (needed since this happens in the background outside it's change detection)
    if (state != null && (JSON.stringify(this.state) != JSON.stringify(state))) {
      // Only rebuild the Assets list if the Assets themselves actually changed. Otherwise, a
      // change elsewhere on the DTO
      const assetsChanged = JSON.stringify(this.state?.assets) != JSON.stringify(state.assets);
      this.state = state;
      // Set the Reader State as well since the reader is already started when you navigate to this page
      this.readerState = state.readerStatus;

      if (assetsChanged) {
        // Preserve the selection of any Asset that was already selected before this refresh
        const previouslySelectedEpcs = new Set(this.assets.filter(asset => asset.selected).map(asset => asset.epc));
        this.assets = this.state?.assets?.map(asset => {
          const assetUI = {
            assetId: asset.assetId,
            epc: asset.epc,
            fnNumber: asset.fnNumber,
            orderNbr: asset.orderNbr,
            inventoryID: asset.inventoryID,
            shipmentNbr: asset.shipmentNbr,
            selected : previouslySelectedEpcs.has(asset.epc),
            status: asset.status,
            inventoryIdInputRequired: asset.inventoryIdInputRequired,
          } as AssetUI;
          return assetUI;
        }) ?? [];
      }
      this.isReaderStateRetrieved = true;
      this.changeDetectorRef.detectChanges();
    }

    // If the component still hasn't been destroyed, then schedule the next refresh
    if (!this.isDestroyed ) {
      setTimeout(this.refreshData.bind(this), RefreshDelayInMs);
    }
  }

  /**
     * Calls the API to re-obtain the reference to the Reader being controlled by the Portal.
     * This is done to obtain the "Reader State" Property Value so we can determine if the 
     * Reader has been started or not. 
     */
  private async refreshReaderState(): Promise<void> {
    // Obtain a fresh reference to the Reader using it's ID...
    let reader: ReaderInstance = null;
    try {
      reader = await this.readerService.getReaderInstanceById(this.reader?.id);
    } catch (error) {
      console.error("Problem obtaining Reader State.", error);
      return;
    }
    this.reader = reader;

    // Get the current state from the Reader Data for the "Reader Status" Property
    const datum = this.reader?.data?.find(datum => datum?.property?.property == ReaderPropertyKey_ReaderStatus);
    if (datum != null) {
      this.readerState = datum.value as AbleNetReaderStateEnum;
      if(this.readerState == AbleNetReaderStateEnum.Reconnecting || this.readerState == AbleNetReaderStateEnum.Restarting) {
        this.isActionBeingPerformed = false;
      }
    }

    // Update the Flag for whether the Reader State has been retrieved or not.
    this.isReaderStateRetrieved = true;
  }

  /**
* Called by the UI when the "Start Reader" button is clicked.
* Kicks off the "Start Reader" Action in the background if no Action
* is currently being performed.
*/
  onStartReaderClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.stopProcessingClicked = false;
      this.startReader();
    }
  }

  /**
   * Performs the "Start" Action on backend which will start the Reader being controlled.
   * In case the handheld is being used, then it will start the validation attempt on the backend so HH can directly send in the reads
   */
  private async startReader(): Promise<void> {
    let result: any = null;
    try {
      result = await this.ablenetExpReturnDevicesService.start(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    if (!this.isHandheldReaderUsed && this.reader != null){
      await this.refreshReaderState();
    }
    this.isActionBeingPerformed = false;
  }

  /**
 * Called by the UI when the "Stop Reader" button is clicked.
 * Kicks off the "Stop Reader" Action in the background if no Action
 * is currently being performed.
 */
  onStopReaderClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.stopProcessingClicked = true;
      this.stopReader();
    }
  }

  /**
 * Performs the "Stop Reader" Action
 */
  private async stopReader(): Promise<void> {
    let result: any = null;
    try {
      result = await this.ablenetExpReturnDevicesService.stop(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    await this.refreshReaderState();
    this.isActionBeingPerformed = false;
  }

    /**
 * Called by the UI when the "Clear Reads" button is clicked.
 * Kicks off the "Clear Reads" Action in the background if no Action
 * is currently being performed.
 */
  onClearReadsClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.stopProcessingClicked = false;
      this.clearReads();
    }
  }

  /**
   * Performs the "Clear Reads" Action
   */
  private async clearReads(): Promise<void> {
    let state: ValidationInfoDto = null;
    try {
      state = await this.ablenetExpReturnDevicesService.clearReads(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }
    this.isActionBeingPerformed = false;
  }

  /**
 * Called by the UI when the "Clear All" button is clicked.
 * Kicks off the "Clear All" Action in the background if no Action
 * is currently being performed.
 */
  onClearAllClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.clearAll();
    }
  }

  /**
   * Performs the "Clear All" Action
  */
  private async clearAll(): Promise<void> {
    let state: ValidationInfoDto = null;
    try {
      state = await this.ablenetExpReturnDevicesService.clearAll(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    this.router.navigate(['ablenet', 'exp-return-devices', 'select-service'], {});
    this.isActionBeingPerformed = false;
  }

/**
* Called by the UI when the "Process Return Devices" button is clicked.
* Kicks off the "Process Return Devices" Action in the background if no Action
* is currently being performed.
*/
  onProcessClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.indefiniteProgressDialog.show("Processing", "Updating Return Devices...");
      this.processReturnDevices();
    }
  }
 
/**
 * Performs the "Process Return Devices" Action
 */
  private async processReturnDevices(): Promise<void> {
    const selectedAssets = this.getSelectedAssets();
    console.log("Selected Assets:", selectedAssets);
    // If the Asset is null, hide the Dialog & display Error Message, also reset state
    if ( selectedAssets == null || selectedAssets.length == 0) {
      this.indefiniteProgressDialog.hide();
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE,"Assets not selected !! Please restart the Reader & select a valid Asset.");
      this.isActionBeingPerformed = false;
      return;
    } else {
          try {
                  await this.ablenetExpReturnDevicesService.processReturnDevices(this.portal.id, selectedAssets);
            } catch (error) {
              this.indefiniteProgressDialog.hide();
              this.isActionBeingPerformed = false;
              console.log(error);
              this.handleError(error).then(() => {
                console.log(error);
                });
            return;
          }
    }

    this.indefiniteProgressDialog.hide();
    this.isActionBeingPerformed = false;
  }

  /**
 * Returns a boolean which indicates if the Reader being controlled by the Portal
 * is started or not.  Used by the UI to disable some controls.
 */
  isReaderStarted(): boolean {
    return this.isReaderStateRetrieved && this.readerState == AbleNetReaderStateEnum.Started;
  }

  /**
   * Returns a boolean which indicates if the "Stop" action can currently be sent to the Reader.
   * Unlike isReaderStarted(), this also allows Stop while the Reader is Reconnecting/Restarting
   * (i.e. temporarily disconnected but expected to resume), so the User can stop it before it
   * comes back online instead of having to wait for it to fully reconnect first.
   */
  canStopReader(): boolean {
    return this.isReaderStateRetrieved && this.readerState != AbleNetReaderStateEnum.Stopped;
  }

  /******************Selection ************************** */
  isSingleAssetSelected(): boolean {
    if (this.assets != null && this.assets.length > 0) {
      let oneSelected = false;
      for (const asset of this.assets) {
        if (asset.selected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyAssetsSelected(): boolean {
    return this.assets.some(asset => asset.selected);
  }

  isAllAssetsSelected(): boolean {
    //console.log("All Assets Selected: ", this.assets && this.assets.every(asset => asset.selected));
    return this.assets !== null && this.assets.every(asset => asset.selected);
  } 

  setAllAssetsSelected(event: any): void {
    this.assets.forEach(asset => asset.selected = event.target.checked);
  }

  trackByAssetEpc(index: number, asset: AssetUI): string {
    return asset.epc;
  }

  getSelectedAssets(): { epc: string, inventoryID?: string }[] {
    return this.assets
      .filter(ui => ui.selected)
      .map(ui => ({ epc: ui.epc, inventoryID: ui.inventoryID }));
  }

  /**
     * Called to handle errors when calling APIs.  
     * Currently this prints them out to the console and displays an alert to the User.
    */ 
  handleError(error: any): Promise<void> {
    console.error(`Error detected: ${error}`);

    if (error?.error != null) {
      return new Promise(resolve => {
        this.simpleSuccessErrorDialogComponent.dialogClosed.subscribe(() => resolve());
        this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
      });
    }

    const response: Response = error;
    alert(response.text());
    return Promise.resolve();
  }


}

class AssetUI {
  selected = false;
  assetId : string;
  epc : string;
  fnNumber?: string;
  orderNbr?: string;
  inventoryID?: string;
  shipmentNbr?: string;
  status?: string;
  inventoryIdInputRequired?: boolean;
}
